import React, { createContext, useContext, useReducer, useEffect, ReactNode } from 'react';
import { User } from '@/types';
import { authAPI, usersAPI } from '@/services/api';
import { notifyWorkspaceSessionChanged } from './workspaceSession';

interface AuthState {
  user: User | null;
  token: string | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  error: string | null;
}

type AuthAction =
  | { type: 'LOGIN_START' }
  | { type: 'LOGIN_SUCCESS'; payload: { user: User; token: string } }
  | { type: 'LOGIN_FAILURE'; payload: string }
  | { type: 'LOGOUT' }
  | { type: 'CLEAR_ERROR' }
  | { type: 'UPDATE_USER'; payload: User };

const initialState: AuthState = {
  user: null,
  token: null,
  isAuthenticated: false,
  isLoading: true,
  error: null,
};

const authReducer = (state: AuthState, action: AuthAction): AuthState => {
  switch (action.type) {
    case 'LOGIN_START':
      return {
        ...state,
        isLoading: true,
        error: null,
      };
    case 'LOGIN_SUCCESS':
      return {
        ...state,
        user: action.payload.user,
        token: action.payload.token,
        isAuthenticated: true,
        isLoading: false,
        error: null,
      };
    case 'LOGIN_FAILURE':
      return {
        ...state,
        user: null,
        token: null,
        isAuthenticated: false,
        isLoading: false,
        error: action.payload,
      };
    case 'LOGOUT':
      return {
        ...state,
        user: null,
        token: null,
        isAuthenticated: false,
        isLoading: false,
        error: null,
      };
    case 'CLEAR_ERROR':
      return {
        ...state,
        error: null,
      };
    case 'UPDATE_USER':
      return {
        ...state,
        user: action.payload,
      };
    default:
      return state;
  }
};

interface AuthContextType extends AuthState {
  login: (email: string, password: string) => Promise<User>;
  logout: () => Promise<void>;
  clearError: () => void;
  updateUser: (user: User) => void;
  startImpersonation: (userId: string) => Promise<void>;
  stopImpersonation: () => Promise<void>;
  isImpersonating: boolean;
  impersonatedName: string;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

const clearStoredSession = () => {
  for (const key of ['token', 'user', 'workspaces', 'currentWorkspaceId', 'impersonating', 'adminWorkspaceId']) {
    localStorage.removeItem(key);
  }
  notifyWorkspaceSessionChanged();
};

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [state, dispatch] = useReducer(authReducer, initialState);

  const impersonation = (() => {
    try { const s = localStorage.getItem('impersonating'); return s ? JSON.parse(s) : null; } catch { return null; }
  })();

  useEffect(() => {
    // Check for existing token on app load
    const token = localStorage.getItem('token');
    const userStr = localStorage.getItem('user');

    if (token && userStr) {
      try {
        const user = JSON.parse(userStr);
        dispatch({
          type: 'LOGIN_SUCCESS',
          payload: { user, token },
        });
      } catch (error) {
        // Invalid stored data, clear it
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        dispatch({ type: 'LOGOUT' });
      }
    } else {
      dispatch({ type: 'LOGOUT' });
    }
  }, []);

  const login = async (email: string, password: string): Promise<User> => {
    dispatch({ type: 'LOGIN_START' });

    try {
      const response = await authAPI.login({ email, password });

      if (response.data) {
        const { token, user, workspaces } = response.data;

        // Store in localStorage
        localStorage.setItem('token', token);
        localStorage.setItem('user', JSON.stringify(user));

        // Store workspace data
        if (workspaces) {
          localStorage.setItem('workspaces', JSON.stringify(workspaces));
          const currentId = localStorage.getItem('currentWorkspaceId');
          const workspace = workspaces.find(item => item.id === currentId) || workspaces[0];
          if (workspace) localStorage.setItem('currentWorkspaceId', workspace.id);
          else localStorage.removeItem('currentWorkspaceId');
        }
        notifyWorkspaceSessionChanged();
        dispatch({
          type: 'LOGIN_SUCCESS',
          payload: { user, token },
        });

        return user;
      } else {
        throw new Error(response.error || 'Login failed');
      }
    } catch (error) {
      clearStoredSession();
      const apiError = (error as { response?: { data?: { error?: unknown } } })?.response?.data?.error;
      const errorMessage = typeof apiError === 'string' ? apiError
        : error instanceof Error ? error.message : 'Login failed';
      dispatch({
        type: 'LOGIN_FAILURE',
        payload: errorMessage,
      });
      throw error;
    }
  };

  const logout = async () => {
    try {
      await authAPI.logout();
    } catch (error) {
      // Ignore logout errors
    } finally {
      // Clear localStorage (including any impersonation markers, so a fresh
      // login on this browser never inherits a stale impersonation banner).
      clearStoredSession();
      dispatch({ type: 'LOGOUT' });
    }
  };

  const clearError = () => {
    dispatch({ type: 'CLEAR_ERROR' });
  };

  const updateUser = (user: User) => {
    localStorage.setItem('user', JSON.stringify(user));
    dispatch({ type: 'UPDATE_USER', payload: user });
  };

  const applySession = (token: string, user: any, workspaces: any[]) => {
    localStorage.setItem('token', token);
    localStorage.setItem('user', JSON.stringify(user));
    localStorage.setItem('workspaces', JSON.stringify(workspaces || []));
    if (workspaces && workspaces.length > 0) localStorage.setItem('currentWorkspaceId', workspaces[0].id);
    else localStorage.removeItem('currentWorkspaceId');
  };

  const startImpersonation = async (userId: string) => {
    const res = await usersAPI.impersonate(userId);
    // Remember the admin's selected workspace so we can restore it on stop.
    const adminWs = localStorage.getItem('currentWorkspaceId');
    if (adminWs) localStorage.setItem('adminWorkspaceId', adminWs);
    applySession(res.token, res.user, res.workspaces);
    localStorage.setItem('impersonating', JSON.stringify({ name: res.user.name }));
    window.location.href = '/';
  };

  const stopImpersonation = async () => {
    const res = await authAPI.stopImpersonation();
    applySession(res.token, res.user, res.workspaces);
    // Restore the admin's previously-selected workspace if it's still valid.
    const adminWs = localStorage.getItem('adminWorkspaceId');
    if (adminWs && (res.workspaces || []).some((w: any) => w.id === adminWs)) {
      localStorage.setItem('currentWorkspaceId', adminWs);
    }
    localStorage.removeItem('adminWorkspaceId');
    localStorage.removeItem('impersonating');
    window.location.href = '/';
  };

  const value: AuthContextType = {
    ...state,
    login,
    logout,
    clearError,
    updateUser,
    startImpersonation,
    stopImpersonation,
    isImpersonating: !!impersonation,
    impersonatedName: impersonation?.name || '',
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
